import React, { useMemo, useState } from 'react';
import Dialog from './Dialog';
import { api } from '../services/api';
import { Delivery } from '../types';

interface Props {
  deliveries: Delivery[];
  /** Ključevi izabranih isporuka, bez prefiksa. */
  selected: string[];
  onToggle: (documentKey: string) => void;

  /**
   * Poziva se kad se spisak promeni — adresa vraćena u red, ili nalog prebacen
   * prodavnici. Oba puta isporuka menja mesto, pa se spisak mora pročitati ponovo.
   */
  onPromena?: () => void;
}

/**
 * B2C isporuke koje čekaju razvoz.
 *
 * Prikazuju se i one koje se ne mogu planirati, sa razlogom. Da se sakriju,
 * operater ne bi znao da mu red za potvrdu adresa zadržava isporuke — video bi
 * kraću listu i mislio da nema posla.
 */
export default function DeliveryPanel({
  deliveries, selected, onToggle, onPromena
}: Props) {
  const [showBlocked, setShowBlocked] = useState(false);

  /**
   * Isporuka kojoj se otključava pin, i zašto.
   *
   * ZAŠTO POSTOJI
   *   Potvrđena adresa je zaključana i do 23.09.2026. se nije mogla otključati sa
   *   ijednog ekrana — pogrešan pin bio je NEPOVRATAN, a vozač bi išao na pogrešno
   *   mesto. Server je to umeo od početka; dugmeta nije bilo.
   *
   *   Izmereno tada: nijedan od 16 izuzetaka sa terena nije bio o adresi, pa je
   *   ovo osiguranje a ne popravka merene greške. Zato stoji TIHO — pojavi se
   *   samo na izabranoj isporuci, da 365 redova ne dobije dugme koje skoro niko
   *   nikad ne pritisne.
   */
  const [otkljucavam, setOtkljucavam] = useState<Delivery | null>(null);
  const [razlog, setRazlog] = useState('');
  const [busy, setBusy] = useState(false);
  const [greska, setGreska] = useState<string | null>(null);

  /**
   * Isporuka koju prodavnica vozi sama, i zašto.
   *
   * ZAŠTO I OVDE
   *   Ista radnja postoji u tabu „Ne može u turu", ali se tamo dođe tek kad
   *   isporuka neće da uđe u turu. Slučaj u kom je prodavnica očigledan izbor —
   *   prodao Apatin, roba u Apatinu, kupac u Apatinu — vidi se bas OVDE, dok se
   *   tura slaže. Tražiti isti nalog u drugom tabu je rad koji ništa ne donosi.
   *
   *   Stoji uz „Pin nije dobar", po istom pravilu: samo na IZABRANOJ isporuci,
   *   da 365 redova ne dobije dugme koje se retko pritiska.
   */
  const [prebacujem, setPrebacujem] = useState<Delivery | null>(null);
  const [prebaciRazlog, setPrebaciRazlog] = useState('');

  const prebaci = async () => {
    if (!prebacujem) {
      return;
    }

    // Šifra objekta je prvi deo oznake otpremnice („007-1-4454" → „007").
    // Isto se radi i u tabu „Ne može u turu" — pravilo je jedno.
    const objekat = prebacujem.documentKey.split('-')[0];

    setBusy(true);
    try {
      await api.moveToLocalDelivery(
        prebacujem.documentKey, objekat, prebaciRazlog.trim());

      setPrebacujem(null);
      setPrebaciRazlog('');
      setGreska(null);
      onPromena?.();
    } catch (e) {
      setGreska(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const otkljucaj = async () => {
    if (!otkljucavam?.addressId || razlog.trim().length === 0) {
      return;
    }

    setBusy(true);
    try {
      await api.unlockAddress(otkljucavam.addressId, razlog.trim());
      setOtkljucavam(null);
      setRazlog('');
      setGreska(null);
      onPromena?.();
    } catch (e) {
      setGreska(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Traženje jedne isporuke po imenu, adresi ili broju otpremnice.
   *
   * ZAŠTO POSTOJI
   *   Prijava sa terena 15.09.2026: „ove prodaje nema kod operatera nigde".
   *   Bila je tu — među 23 zaglavljene isporuke, u sklopljenom bloku. Kad kupac
   *   pozove i pita gde mu je roba, operater nije imao čime da je potraži nego
   *   je čitao spisak redom.
   *
   *   Brojke po razlozima odgovaraju na pitanje „gde je usko grlo". Ne
   *   odgovaraju na „gde je Mešaroš Oršolja", a to je pitanje koje stiže
   *   telefonom.
   */
  const [trazim, setTrazim] = useState('');

  const pojam = trazim.trim().toLowerCase();

  const odgovara = useMemo(() => (d: Delivery) => {
    if (pojam === '') {
      return true;
    }

    return [d.customer, d.address, d.city, d.documentKey]
      .some((polje) => (polje ?? '').toLowerCase().includes(pojam));
  }, [pojam]);

  const { ready, zaObjekte, blocked } = useMemo(
    () => ({
      // Roba za kupca koja ide U OBJEKAT se bira uz tok tog objekta (levo), pa
      // ovde ne stoji kao kupčeva adresa — samo kao podsetnik gde je.
      ready: deliveries.filter((d) => d.canBePlanned && !d.uObjekat && odgovara(d)),
      zaObjekte: deliveries.filter((d) => d.canBePlanned && !!d.uObjekat && odgovara(d)),
      blocked: deliveries.filter((d) => !d.canBePlanned && odgovara(d))
    }),
    [deliveries, odgovara]
  );

  // Razlozi se broje, jer operatera zanima gde je usko grlo, a ne spisak od
  // četrdeset redova sa istom rečenicom.
  const obstacles = useMemo(() => {
    const counts = new Map<string, number>();
    for (const delivery of blocked) {
      const reason = delivery.obstacle ?? 'nepoznato';
      counts.set(reason, (counts.get(reason) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [blocked]);

  // Dok se traži, zaglavljeni se otvaraju sami. Inače bi pretraga našla red i
  // ostavila ga sakrivenim — što je tačno ono od čega je i krenula.
  const zaglavljeniOtvoreni = showBlocked || pojam !== '';

  if (deliveries.length === 0) {
    return <p className="empty">Nema B2C isporuka koje čekaju.</p>;
  }

  return (
    <>
      <div className="deliveries__trazi">
        <input
          type="search"
          value={trazim}
          onChange={(e) => setTrazim(e.target.value)}
          placeholder="Nađi kupca, ulicu ili otpremnicu"
          aria-label="Nađi isporuku"
        />
        {pojam !== '' && (
          <span className="muted">
            {ready.length + zaObjekte.length + blocked.length === 0
              ? 'ništa nije nađeno'
              : `nađeno: ${ready.length} spremno, `
                + (zaObjekte.length > 0 ? `${zaObjekte.length} za objekte, ` : '')
                + `${blocked.length} zaglavljeno`}
          </span>
        )}
      </div>

      {ready.length === 0 ? (
        <p className="empty">
          {pojam === ''
            ? 'Nijedna isporuka nije spremna za turu.'
            : 'Nijedna spremna isporuka ne odgovara traženom.'}
        </p>
      ) : (
        <ul className="deliveries">
          {ready.map((delivery) => (
            <li key={delivery.documentKey}>
              <button
                type="button"
                className={
                  'delivery'
                  + (selected.includes(delivery.documentKey) ? ' delivery--selected' : '')
                  + (delivery.isOverdue ? ' delivery--overdue' : '')
                }
                onClick={() => onToggle(delivery.documentKey)}
              >
                <span className="delivery__address">
                  {delivery.address}, {delivery.city}
                </span>
                <span className="delivery__age">
                  {delivery.waitingDays} {delivery.waitingDays === 1 ? 'dan' : 'dana'}
                </span>
                <span className="delivery__meta">
                  {delivery.customer || 'bez imena'} · {delivery.pieces} kom
                  {delivery.requiresTwoPeople && <strong> · dva čoveka</strong>}
                  {delivery.floor !== null && delivery.floor > 0 && (
                    <> · {delivery.floor}. sprat{delivery.hasLift ? ', lift' : ', bez lifta'}</>
                  )}
                  {/* Ko je prodao. Ako nije centralni magacin, roba se možda
                      diže u tom objektu — aplikacija to ne zna, operater zna. */}
                  {delivery.originName && <> · prodao {delivery.originName}</>}
                </span>
                {/* GDE ROBA STOJI. Prodavac to ne govori: aparat prodat u
                    Apatinu ume da leži u Odžacima, a od toga zavisi koja tura
                    uopšte može da ga ponese. */}
                {/* Trgovac je hteo da kaže „kupac nosi sam" pa promašio
                    cifru. Ne odlučujemo umesto njega — samo pitamo. */}
                {delivery.noteLooksLikeSelfPickup && (
                  <span className="delivery__where">
                    <strong>proveri napomenu:</strong> „{delivery.notes}" liči na
                    oznaku samovoza, ali nije nijedna od važećih
                  </span>
                )}
                {/* Odluka pokazuje na magacin gde robe vise nema. Ne blokira
                    — poslovodja koji robu vidi na polici sme da potvrdi i kad
                    zaliha kaze nulu — ali vozilo ne sme da ode po nista. */}
                {delivery.warning && (
                  <span className="delivery__where">
                    <strong>proveri izvor:</strong> {delivery.warning}
                  </span>
                )}
                {/* Isporuka koja ide nepotpuna, covekovom odlukom. Vozilo
                    ce na tu adresu izaci jos jednom — neka se vidi. */}
                {delivery.ostajeZaKasnije && delivery.ostajeZaKasnije.length > 0 && (
                  <span className="delivery__where">
                    <strong>ide bez:</strong> {delivery.ostajeZaKasnije.join(', ')} —
                    kupac pristao da sačeka
                  </span>
                )}
                {/* Predato je i zavrseno; ERP to jos ne zna jer kolonu kukici
                    covek. Stoji da operater ne bi planirao istu robu drugi put
                    — i da zna kome da kaze da proknjizi. */}
                {delivery.vecIsporuceno && delivery.vecIsporuceno.length > 0 && (
                  <span className="delivery__where">
                    <strong>već isporučeno:</strong> {delivery.vecIsporuceno.join(', ')} —
                    čeka se knjiženje u ERP-u
                  </span>
                )}
                {delivery.sourcing && (
                  <span className="delivery__where">
                    {delivery.sourcing.singleStop.length > 0
                      ? <>roba: {delivery.sourcing.singleStop.join(' ili ')}</>
                      : <strong>roba razbacana: {delivery.sourcing.allSources.join(', ')}</strong>}
                  </span>
                )}
              </button>

              {/* Ceo red je dugme za izbor, pa ovo ne može unutra. Pojavljuje se
                  samo na IZABRANOJ isporuci: pogrešan pin je redak, a dugme na
                  svakom redu bilo bi buka koja sakrije ono što se gleda svaki dan. */}
              {selected.includes(delivery.documentKey) && (
                <p className="delivery__fix">
                  {delivery.addressId !== null && (
                    <button
                      type="button"
                      onClick={() => { setOtkljucavam(delivery); setRazlog(''); }}
                    >
                      Pin nije dobar — vrati adresu u red
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => { setPrebacujem(delivery); setPrebaciRazlog(''); }}
                  >
                    Vozi prodavnica sama
                  </button>
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {zaObjekte.length > 0 && (
        <div className="zaobjekte">
          <p className="note">
            <strong>Roba za kupce u objektu · {zaObjekte.length}</strong> — kupac dolazi sam
            (ili ga vozi prodavnica), a roba stoji drugde. Bira se uz tok tog objekta u
            listi internih prenosa, pod oznakom „za kupca".
          </p>
          <ul className="rows">
            {zaObjekte.map((delivery) => (
              <li key={delivery.documentKey}>
                <span className="row__name">
                  {delivery.customer || '(bez imena)'}
                  <span className="muted">
                    {' · '}za {delivery.uObjekat!.naziv}{' · '}{delivery.documentKey}
                  </span>
                </span>
                <span className="row__meta">
                  {delivery.pieces} kom iz {delivery.uObjekat!.izvor} · čeka {delivery.waitingDays}{' '}
                  {delivery.waitingDays === 1 ? 'dan' : 'dana'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {prebacujem && (
        <Dialog
          title="Vozi prodavnica sama"
          description={
            <>
              <strong>{prebacujem.customer}</strong> — {prebacujem.address}, {prebacujem.city}
              <br /><br />
              Nalog izlazi iz tvog planiranja i pojavljuje se poslovođi na kartici{' '}
              <strong>„Moj razvoz"</strong>. Roba je kod njega, pa se ništa ne diže iz
              magacina.
              <br /><br />
              Ako prodavnica ipak ne može — vozilo u servisu, nema ko — nalog se vraća
              tebi istim dugmetom, u tabu „Ne može u turu".
            </>
          }
          confirmLabel="Prebaci prodavnici"
          busy={busy}
          onCancel={() => { setPrebacujem(null); setPrebaciRazlog(''); setGreska(null); }}
          onConfirm={() => void prebaci()}
        >
          <label>
            Zašto (može ostati prazno)
            <input
              value={prebaciRazlog}
              onChange={(e) => setPrebaciRazlog(e.target.value)}
              placeholder="npr. kupac ne može da čeka do četvrtka"
            />
          </label>

          {greska && <p className="health health--bad">{greska}</p>}
        </Dialog>
      )}

      {otkljucavam && (
        <Dialog
          title="Vrati adresu u red za potvrđivanje"
          description={
            <>
              <strong>{otkljucavam.address}, {otkljucavam.city}</strong>
              <br />
              Adresa se vraća u red i isporuka dotad ne može na turu — nesigurna
              adresa nikad ne ulazi u rutu. Koordinata se ne briše: u redu je vidiš
              kao predlog i pomeriš pin.
              <br />
              Razlog ostaje uz adresu, jer je ovo ispravka tuđe potvrde.
            </>
          }
          confirmLabel="Vrati u red"
          busy={busy}
          disabled={razlog.trim().length === 0}
          onCancel={() => { setOtkljucavam(null); setRazlog(''); setGreska(null); }}
          onConfirm={() => void otkljucaj()}
        >
          <label>
            Zašto pin nije dobar
            <textarea
              value={razlog}
              onChange={(e) => setRazlog(e.target.value)}
              placeholder="npr. kupac javio da je pin na susednoj ulici"
              rows={3}
            />
          </label>

          {greska && <span className="health health--bad">{greska}</span>}
        </Dialog>
      )}

      {blocked.length > 0 && (
        <div className="blockedlist">
          <button
            type="button"
            className="disc__head"
            aria-expanded={zaglavljeniOtvoreni}
            onClick={() => setShowBlocked((v) => !v)}
          >
            <span className="disc__caret" aria-hidden="true">›</span>
            <span className="disc__title">{blocked.length} isporuka ne može na turu</span>
          </button>

          <ul className="blockedlist__reasons">
            {obstacles.map(([reason, count]) => (
              <li key={reason}>
                <span className="blockedlist__count">{count}</span> {reason}
              </li>
            ))}
          </ul>

          {zaglavljeniOtvoreni && (
            <ul className="rows disc__body">
              {blocked.map((delivery) => (
                <li key={delivery.documentKey}>
                  {/* IME KUPCA stoji i ovde. Bez njega se red nije mogao naci
                      ni pretragom ni okom: kad kupac pozove, operater zna
                      njegovo ime, ne ulicu. */}
                  <span className="row__name">
                    {delivery.customer || '(bez imena)'}
                    <span className="muted">
                      {delivery.uObjekat
                        ? <>{' · '}za {delivery.uObjekat.naziv} ({delivery.uObjekat.vrsta === 'ProdavnicaVozi'
                            ? 'prodavnica vozi kupcu'
                            : 'kupac dolazi u objekat'})</>
                        : <>{' · '}{delivery.address || '(bez ulice)'}, {delivery.city || '(bez mesta)'}</>}
                      {' · '}{delivery.documentKey}
                    </span>
                  </span>
                  <span className="row__meta">{delivery.obstacle}</span>
                  {/* „Nema robe" sa mestima: obećano drugde, razbacano po
                      komad… Bez ovoga se naručivalo ono što već postoji. */}
                  {delivery.sourcing?.whyMissing && delivery.sourcing.whyMissing.length > 0 && (
                    <span className="row__meta muted">
                      {delivery.sourcing.whyMissing.join('; ')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
